import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import Header from '@/components/layout/Header'
import Panel, { panelGhostButton, panelPrimaryButton } from '@/components/layout/Panel'
import MyPositions from '@/components/members/MyPositions'
import SteamIcon from '@/components/site/SteamIcon'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { memberSteamProfileUrl } from '@/lib/steam'

interface SteamState {
  linked: boolean
  name: string
  verified: boolean
}

/** Kişinin kendisiyle ilgili ayarlar: oynamak istediği pozisyonlar ve Steam hesabı. */
export default function Profile() {
  const { user } = useAuth()
  const { T } = useLanguage()
  const [steam, setSteam] = useState<SteamState | null>(null)
  const [message, setMessage] = useState('')

  const loadSteam = () =>
    fetch('/api/steam/me', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then((d: SteamState | null) => { if (d) setSteam(d) })
      .catch(() => { /* çevrimdışı: kart boş kalır, yanlış bir şey söylemez */ })

  useEffect(() => { void loadSteam() }, [])

  // Steam /profile?steam=linked|failed|taken ile geri döner. Sonuç bir kez
  // gösterilir, yenilemede eski mesaj tekrarlanmasın diye parametre düşer.
  const [params, setParams] = useSearchParams()
  const steamResult = params.get('steam')
  useEffect(() => {
    if (!steamResult) return
    setMessage(
      steamResult === 'linked' ? T.members.steamLinkedToast
      : steamResult === 'taken' ? T.members.steamTakenToast
      : T.members.steamFailedToast
    )
    const next = new URLSearchParams(params)
    next.delete('steam')
    setParams(next, { replace: true })
  }, [steamResult, params, setParams, T])

  async function unlink() {
    try {
      const res = await fetch('/api/steam', { method: 'DELETE', credentials: 'include' })
      if (!res.ok) throw new Error(String(res.status))
      setMessage('')
      await loadSteam()
    } catch {
      setMessage(T.members.manage.steamError)
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Header title={T.nav.profile} />
      <div className="flex-1 space-y-4 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          {user && (
            <div className="flex items-center gap-3">
              <img
                src={user.avatar}
                alt=""
                className="h-12 w-12 shrink-0 rounded-full object-cover ring-1 ring-white/15"
                onError={e => { (e.target as HTMLImageElement).src = 'https://cdn.discordapp.com/embed/avatars/0.png' }}
              />
              <div className="min-w-0">
                <p className="truncate text-base font-semibold">{user.name}</p>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  {T.roles[user.role as keyof typeof T.roles] ?? user.role}
                </p>
              </div>
            </div>
          )}

          {message && (
            <p className="rounded-sm border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              {message}
            </p>
          )}

          <Panel title={T.members.positions.mine} hint={T.members.positions.mineHint}>
            <MyPositions />
          </Panel>

          <Panel
            title={T.members.steamAccount}
            hint={steam && !steam.verified ? T.members.steamLinkBody : undefined}
          >
            {steam && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {steam.linked ? (
                  <>
                    <SteamIcon size={14} />
                    <a
                      href={user ? memberSteamProfileUrl(user.id) : undefined}
                      target="_blank"
                      rel="noreferrer"
                      title={T.members.steamOpenProfile}
                      className="min-w-0 truncate text-sm font-medium underline decoration-dotted underline-offset-2 transition-colors hover:text-primary"
                    >
                      {steam.name || T.members.steamNoName}
                    </a>
                    {steam.verified
                      ? <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary" aria-label={T.members.steamVerified} />
                      : <span className="shrink-0 text-[10px] uppercase tracking-wider text-amber-400">{T.members.steamUnverified}</span>}
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">{T.members.steamNotLinked}</span>
                )}
                <div className="ml-auto flex flex-wrap items-center gap-1.5">
                  <a
                    href="/api/steam/link"
                    className={steam.verified ? panelGhostButton : panelPrimaryButton}
                  >
                    <SteamIcon size={12} />
                    {steam.verified ? T.members.steamRelinkCta : T.members.steamLinkCta}
                  </a>
                  {steam.linked && (
                    <button type="button" onClick={() => void unlink()} className={panelGhostButton}>
                      {T.members.steamUnlinkCta}
                    </button>
                  )}
                </div>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}
